import type {
  PasswordResetProof,
  Callout,
  CatalogGrenade,
  GrenadeCatalog,
  MapDefinition,
  MediaAsset,
  Member,
  PublishedSnapshot,
  Session,
  SnapshotPayload,
  SnapshotSummary,
  Tactic,
  Utility,
  UtilityPhysicsV2,
} from "@cs2/contracts";
import {
  mapSchema,
  catalogGrenadeSchema,
  grenadeCatalogSchema,
  mediaAssetSchema,
  memberSchema,
  publishedSnapshotSchema,
  sessionSchema,
  snapshotPayloadSchema,
  snapshotSummarySchema,
  tacticSchema,
  utilityPhysicsV2Schema,
  utilitySchema,
} from "@cs2/contracts";
import { request } from "./request.js";

export async function getSnapshot(
  init?: Pick<RequestInit, "cache">,
): Promise<PublishedSnapshot> {
  return publishedSnapshotSchema.parse(
    await (await request("/api/public/snapshot", init)).json(),
  );
}

/**
 * Karten inkl. Callouts live statt aus dem eingefrorenen Snapshot — Callouts
 * sind globale Referenzdaten und sollen ohne "Veröffentlichen" sofort sichtbar
 * sein.
 */
export async function getPublicMaps(
  init?: Pick<RequestInit, "cache">,
): Promise<MapDefinition[]> {
  return mapSchema
    .array()
    .parse(await (await request("/api/public/maps", init)).json());
}

export async function getSession(): Promise<Session> {
  return sessionSchema.parse(await (await request("/api/auth/session")).json());
}

/**
 * Der Access-Cookie läuft nach einer Stunde ab. Ohne aktiven Refresh liefe jede
 * längere Editor-Sitzung in einen stillen 401 beim nächsten Speichern.
 */
export async function refreshSession(csrfToken: string): Promise<Session> {
  return sessionSchema.parse(
    await (
      await request("/api/auth/refresh", {
        method: "POST",
        headers: { "x-csrf-token": csrfToken },
      })
    ).json(),
  );
}

export async function logout(csrfToken: string): Promise<void> {
  await request("/api/auth/logout", {
    method: "POST",
    headers: { "x-csrf-token": csrfToken },
  });
}

export async function login(email: string, password: string): Promise<Session> {
  return sessionSchema.parse(
    await (
      await request("/api/auth/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, password }),
      })
    ).json(),
  );
}

export async function getEditorPayload(): Promise<SnapshotPayload> {
  return snapshotPayloadSchema.parse(
    await (await request("/api/editor/bootstrap")).json(),
  );
}

export async function getGrenadeCatalog(): Promise<GrenadeCatalog> {
  return grenadeCatalogSchema.parse(
    await (await request("/api/grenades", { cache: "no-store" })).json(),
  );
}

export async function createCatalogGrenade(
  utility: Utility,
  csrfToken: string,
): Promise<CatalogGrenade> {
  return catalogGrenadeSchema.parse(
    await (
      await request("/api/grenades", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "x-csrf-token": csrfToken,
        },
        body: JSON.stringify(utility),
      })
    ).json(),
  );
}

export async function saveCatalogGrenade(
  utility: CatalogGrenade,
  csrfToken: string,
): Promise<CatalogGrenade> {
  return catalogGrenadeSchema.parse(
    await (
      await request(`/api/grenades/${encodeURIComponent(utility.id)}`, {
        method: "PUT",
        headers: {
          "content-type": "application/json",
          "if-match": String(utility.version),
          "x-csrf-token": csrfToken,
        },
        body: JSON.stringify(utility),
      })
    ).json(),
  );
}

export async function deleteCatalogGrenade(
  id: string,
  csrfToken: string,
): Promise<void> {
  await request(`/api/grenades/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: { "x-csrf-token": csrfToken },
  });
}

export async function createTactic(
  tactic: Tactic,
  csrfToken: string,
): Promise<Tactic> {
  return tacticSchema.parse(
    await (
      await request("/api/editor/tactics", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "x-csrf-token": csrfToken,
        },
        body: JSON.stringify(tactic),
      })
    ).json(),
  );
}

export async function saveTactic(
  tactic: Tactic,
  csrfToken: string,
): Promise<Tactic> {
  return tacticSchema.parse(
    await (
      await request(`/api/editor/tactics/${encodeURIComponent(tactic.id)}`, {
        method: "PUT",
        headers: {
          "content-type": "application/json",
          "if-match": String(tactic.version),
          "x-csrf-token": csrfToken,
        },
        body: JSON.stringify(tactic),
      })
    ).json(),
  );
}

export async function approveTactic(
  tactic: Tactic,
  csrfToken: string,
): Promise<Tactic> {
  return tacticSchema.parse(
    await (
      await request(
        `/api/editor/tactics/${encodeURIComponent(tactic.id)}/approve`,
        {
          method: "POST",
          headers: {
            "if-match": String(tactic.version),
            "x-csrf-token": csrfToken,
          },
        },
      )
    ).json(),
  );
}

export async function createUtility(
  utility: Utility,
  csrfToken: string,
): Promise<Utility> {
  return utilitySchema.parse(
    await (
      await request("/api/editor/utilities", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "x-csrf-token": csrfToken,
        },
        body: JSON.stringify(utility),
      })
    ).json(),
  );
}

export async function saveUtility(
  utility: Utility,
  csrfToken: string,
): Promise<Utility> {
  return utilitySchema.parse(
    await (
      await request(`/api/editor/utilities/${encodeURIComponent(utility.id)}`, {
        method: "PUT",
        headers: {
          "content-type": "application/json",
          "if-match": String(utility.version),
          "x-csrf-token": csrfToken,
        },
        body: JSON.stringify(utility),
      })
    ).json(),
  );
}

export async function previewUtilityPhysics(
  utility: Utility,
  csrfToken: string,
): Promise<UtilityPhysicsV2 | null> {
  const body: unknown = await (
    await request("/api/editor/utilities/physics-preview", {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "x-csrf-token": csrfToken,
      },
      body: JSON.stringify(utility),
    })
  ).json();
  return body === null ? null : utilityPhysicsV2Schema.parse(body);
}

export async function saveMapCallouts(
  mapId: string,
  callouts: Record<string, Callout>,
  expectedVersion: number,
  csrfToken: string,
): Promise<MapDefinition> {
  return mapSchema.parse(
    await (
      await request(`/api/editor/maps/${encodeURIComponent(mapId)}/callouts`, {
        method: "PUT",
        headers: {
          "content-type": "application/json",
          "if-match": String(expectedVersion),
          "x-csrf-token": csrfToken,
        },
        body: JSON.stringify(callouts),
      })
    ).json(),
  );
}

export interface CropPayload {
  x: number;
  y: number;
  width: number;
  height: number;
  zoom: number;
  rotation: 0 | 90 | 180 | 270;
}

export async function uploadMedia(
  file: File,
  crop: CropPayload,
  csrfToken: string,
): Promise<MediaAsset> {
  const formData = new FormData();
  formData.append("crop", JSON.stringify(crop));
  formData.append("file", file, file.name);
  return mediaAssetSchema.parse(
    await (
      await request("/api/editor/media", {
        method: "POST",
        headers: { "x-csrf-token": csrfToken },
        body: formData,
      })
    ).json(),
  );
}

export async function publishSnapshot(
  csrfToken: string,
): Promise<PublishedSnapshot> {
  return publishedSnapshotSchema.parse(
    await (
      await request("/api/editor/publish", {
        method: "POST",
        headers: { "x-csrf-token": csrfToken },
      })
    ).json(),
  );
}

export async function listSnapshots(): Promise<SnapshotSummary[]> {
  return snapshotSummarySchema
    .array()
    .parse(await (await request("/api/editor/snapshots")).json());
}

/** Markiert eine Revision für die Historienverwaltung. */
export async function activateSnapshot(
  revision: number,
  csrfToken: string,
): Promise<PublishedSnapshot> {
  return publishedSnapshotSchema.parse(
    await (
      await request(`/api/editor/snapshots/${revision}/activate`, {
        method: "POST",
        headers: { "x-csrf-token": csrfToken },
      })
    ).json(),
  );
}

export async function deleteTactic(
  id: string,
  csrfToken: string,
): Promise<void> {
  await request(`/api/editor/tactics/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: { "x-csrf-token": csrfToken },
  });
}

export async function deleteUtility(
  id: string,
  csrfToken: string,
): Promise<void> {
  await request(`/api/editor/utilities/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: { "x-csrf-token": csrfToken },
  });
}

export async function listMembers(): Promise<Member[]> {
  return memberSchema
    .array()
    .parse(await (await request("/api/editor/members")).json());
}

export async function inviteMember(
  email: string,
  role: Member["role"],
  csrfToken: string,
): Promise<Member> {
  return memberSchema.parse(
    await (
      await request("/api/editor/members", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "x-csrf-token": csrfToken,
        },
        body: JSON.stringify({ email, role }),
      })
    ).json(),
  );
}

export async function updateMember(
  userId: string,
  patch: { role?: Member["role"]; active?: boolean },
  csrfToken: string,
): Promise<Member> {
  return memberSchema.parse(
    await (
      await request(`/api/editor/members/${encodeURIComponent(userId)}`, {
        method: "PATCH",
        headers: {
          "content-type": "application/json",
          "x-csrf-token": csrfToken,
        },
        body: JSON.stringify(patch),
      })
    ).json(),
  );
}

export async function requestPasswordReset(email: string): Promise<void> {
  await request("/api/auth/password-reset", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email }),
  });
}

export async function completePasswordReset(
  proof: PasswordResetProof,
  password: string,
): Promise<void> {
  await request("/api/auth/password-reset/confirm", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ ...proof, password }),
  });
}
